import type { FC } from 'react';
import { useBlockEditFormStore } from '@ContractBuilder/modules/block-edit';
import { useEntityStore } from '@ContractBuilder/store';
import { useCurrentContractInlineAttachments } from '@hooks/useCurrentContractInlineAttachments';
import { isNonNullish, isSummarySection } from '@root/helpers';
import { canChange } from '@root/src/helpers/canChange';
import { useCurrentContractSections, useDocumentLocation, useFeatureFlags } from '@src/hooks';

import { useEndorsementNavSections } from '../hooks/use-endorsement-nav-sections';
import { isEndorsementDetailsNavItem, type NavItem } from '../types';
import { Navigation } from '../views/Navigation';

interface NavigationProps {
  activeSectionId?: string;
}

export const NavigationController: FC<NavigationProps> = ({ activeSectionId }) => {
  const submission = useEntityStore((state) => state.submission);

  const { isEndorsementView } = useDocumentLocation();
  const contractSections = useCurrentContractSections();
  const endorsementNavSections = useEndorsementNavSections();
  const attachments = useCurrentContractInlineAttachments();
  const hasAttachmentsFeature = useFeatureFlags()('Attachments');
  const hasAttachments = hasAttachmentsFeature && (submission?.attachments?.length ?? 0) > 0;

  if (!submission) {
    throw new Error(`Contract not found`);
  }

  const sections: NavItem[] = isEndorsementView ? endorsementNavSections : contractSections;

  const editingBlockId = useBlockEditFormStore((state) => state.formValues?.id);
  const isEditing = isNonNullish(editingBlockId);
  const isGloballyDisabledByStatus = !canChange(submission?.status);
  const isDragDisabled = isGloballyDisabledByStatus || isEditing;

  const getShouldDisableOperations = (item: NavItem) => {
    if (isGloballyDisabledByStatus) {
      return false;
    }

    if (isEditing) {
      return true;
    }

    // Endorsement details nav item is draggable as a unit but individual operations are disabled
    if (isEndorsementDetailsNavItem(item)) {
      return true;
    }

    // Non-summary sections in endorsement view have operations disabled
    return isEndorsementView && !isSummarySection(item);
  };

  return (
    <Navigation
      activeSectionId={activeSectionId}
      hasAttachments={hasAttachments}
      isDragDisabled={isDragDisabled}
      sections={sections}
      attachments={attachments}
      getShouldDisableOperations={getShouldDisableOperations}
    />
  );
};
